/**
 * Shared UI helpers for the /performance module.
 *
 * Contains the design tokens (semaphore CSS classes, period list, stage
 * icons, conversion labels) and small presentational primitives used
 * across every sub-component of the performance dashboard.
 */
import {
  Users2, PhoneCall, Send, Calendar, Award, ClipboardCheck, FileText, CheckSquare,
  ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";

export const PERIODS = [
  { k: "today",       t: "Hoje" },
  { k: "yesterday",   t: "Ontem" },
  { k: "week",        t: "Esta semana" },
  { k: "prev_week",   t: "Semana anterior" },
  { k: "month",       t: "Este mês" },
  { k: "prev_month",  t: "Mês anterior" },
  { k: "last_30d",    t: "Últimos 30 dias" },
  { k: "last_90d",    t: "Últimos 90 dias" },
  { k: "all",         t: "Total" },
];

export const CLASS_TEXT = {
  excellent: "text-[hsl(var(--success))]", target: "text-[hsl(var(--success))]",
  attention: "text-[hsl(var(--warning))]", critical: "text-[hsl(var(--danger))]", unknown: "text-muted-foreground",
};

export const CLASS_BG = {
  excellent: "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25",
  target: "bg-card border-[hsl(var(--success))]/25",
  attention: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25",
  critical: "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25",
  unknown: "bg-card border-border",
};

export const CLASS_DOT = {
  excellent: "bg-emerald-500", target: "bg-emerald-500",
  attention: "bg-amber-500", critical: "bg-red-500", unknown: "bg-[#94A3B8]",
};

export const STAGE_ICONS = {
  leads_received: Users2, leads_contacted: PhoneCall, leads_qualified: CheckSquare,
  estimates_sent: Send, rt_scheduled: Calendar, rt_realised: ClipboardCheck,
  proposals_sent: FileText, deals_closed: Award,
};

export const CONV_LABELS = {
  lead_to_contact: "Lead → Contacto",
  contact_to_qualified: "Contacto → Qualificado",
  qualified_to_estimate: "Qualificado → Estimativa",
  estimate_to_rt: "Estimativa → RT",
  rt_scheduled_to_realised: "RT agendada → realizada",
  rt_to_proposal: "RT → Proposta",
  proposal_to_deal: "Proposta → Negócio",
};

export function fmtNumber(n) {
  return new Intl.NumberFormat("pt-PT").format(Number(n || 0));
}

export function slaSemaphoreClass(pct) {
  if (pct >= 90) return "target";
  if (pct >= 80) return "attention";
  return "critical";
}

export function DeltaTag({ delta, invert = false }) {
  if (!delta || delta.pct === null || delta.pct === undefined) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const Icon = delta.direction === "up" ? ArrowUpRight :
                delta.direction === "down" ? ArrowDownRight : Minus;
  // "up" is good for most metrics — invert=true flips the coloring
  // (e.g. for "leads perdidos" going up is bad).
  const goodUp = !invert;
  const color = delta.direction === "up"
    ? (goodUp ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]")
    : delta.direction === "down"
      ? (goodUp ? "text-[hsl(var(--danger))]" : "text-[hsl(var(--success))]")
      : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {Math.abs(delta.pct).toFixed(1)}%
    </span>
  );
}
